import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { API_BASE } from "../config";
import { apiFetch } from "../utilities/apiFetch";
import { leerReporteBanorte } from "../utilities/conciliacionArchivo";
import {
  analizarConciliacion, registrosDeBd, segundos, segundosDeBd, fechaHoraMx, fechaMx, fechaIso,
  etiquetaDeFecha, etiquetaDeFechaHora, scriptsEnUnArchivo, matDeRef1, plantelOficial, clavesDeRef1, ENCABEZADOS_CASOS,
} from "../utilities/conciliacionPagos";

// Modulo "Conciliacion" (solo Administrador). Compara los pagos en linea guardados en la BD contra el reporte de
// Banorte (Transacciones Payworks 2) y muestra que registros dicen "pagado" sin serlo y que pagos aprobados no
// quedaron registrados. NO modifica nada: entrega los scripts SQL y los archivos por plantel; las correcciones se
// aplican corriendo los scripts en la base de datos. La logica esta en utilities/conciliacionPagos.js.

const hoyISO = () => {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;
};
const primerDiaDelMes = () => `${hoyISO().substring(0, 8)}01`;
const dinero = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FILAS_VISIBLES = 50;

const partesFecha = (iso) => iso.split("-").map(Number);
const inicioDelDia = (iso) => { const [a, m, d] = partesFecha(iso); return segundos(a, m, d, 0, 0, 0); };
const finDelDia = (iso) => { const [a, m, d] = partesFecha(iso); return segundos(a, m, d, 23, 59, 59); };

function descargarArchivo(nombre, contenido, tipo) {
  const url = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const estiloTarjeta = { border: "1px solid #ddd", borderRadius: "8px", padding: "14px", backgroundColor: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" };
const estiloInput = { padding: "10px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "1rem" };
const estiloTh = { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #ddd", fontSize: "0.8rem", whiteSpace: "nowrap" };
const estiloTd = { padding: "6px 8px", borderBottom: "1px solid #eee", fontSize: "0.85rem", verticalAlign: "top" };

const Tarjeta = ({ titulo, valor, detalle, color }) => (
  <div style={{ ...estiloTarjeta, borderLeft: `5px solid ${color}` }}>
    <div style={{ fontSize: "0.8rem", color: "#555" }}>{titulo}</div>
    <div style={{ fontSize: "1.6rem", fontWeight: "bold", color: "#222" }}>{valor}</div>
    {detalle && <div style={{ fontSize: "0.8rem", color: "#666" }}>{detalle}</div>}
  </div>
);

const Aviso = ({ children, tipo = "info" }) => (
  <div style={{
    margin: "8px 0", padding: "10px", borderRadius: "4px", fontSize: "0.85rem",
    backgroundColor: tipo === "error" ? "#fdecea" : "#fff8e1", border: `1px solid ${tipo === "error" ? "#f5c6cb" : "#ffe082"}`,
    color: tipo === "error" ? "#7a1c1c" : "#7a5c00",
  }}>{children}</div>
);

const VistaConciliacion = ({ catalogoPlanteles = [], catalogoConceptos = [] }) => {
  const [fechaInicio, setFechaInicio] = useState(primerDiaDelMes());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [archivo, setArchivo] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  // oficial ("07") -> nombre; el id de plantel en la BD va corrido en 2 respecto al oficial
  const nombresPlanteles = useMemo(() => {
    const nombres = {};
    catalogoPlanteles.forEach((p) => {
      const oficial = Number(p.id) - 2;
      if (oficial > 0) nombres[String(oficial).padStart(2, "0")] = String(p.nombre_plantel || "").trim();
    });
    return nombres;
  }, [catalogoPlanteles]);

  const precios = useMemo(() => {
    const mapa = {};
    catalogoConceptos.forEach((c) => {
      if (c.clave !== undefined && c.clave !== null && c.precio !== undefined && !Number.isNaN(Number(c.precio))) mapa[String(c.clave)] = Number(c.precio);
    });
    return mapa;
  }, [catalogoConceptos]);

  const rangoInvalido = !fechaInicio || !fechaFin || fechaInicio > fechaFin;

  const comparar = async () => {
    setError("");
    setResultado(null);
    if (!archivo) { setError("Elige el reporte de Banorte (CSV o Excel)."); return; }
    setTrabajando(true);
    try {
      const todasLasTransacciones = await leerReporteBanorte(archivo);

      const res = await apiFetch(`${API_BASE}/wMultipagosConciliacionPagos.php?${new URLSearchParams({ fecha_inicio: fechaInicio, fecha_fin: fechaFin })}`);
      const json = await res.json();
      if (!json.success) { setError(json.message || "No se pudieron consultar los pagos de la base de datos."); return; }

      const ahoraTexto = String(json.ahora || "");
      const ahora = segundosDeBd(ahoraTexto.substring(0, 10), ahoraTexto.substring(11));
      if (ahora === null) { setError("El servidor no mando su fecha y hora; no se puede continuar."); return; }

      const inicio = inicioDelDia(fechaInicio);
      const finDia = finDelDia(fechaFin);
      const transacciones = todasLasTransacciones.filter((t) => t.ts >= inicio && t.ts <= finDia);
      if (!transacciones.length) { setError("El reporte de Banorte no trae transacciones dentro de las fechas elegidas. Revisa que sea del mismo periodo."); return; }

      // El reporte del banco es la referencia: lo que pase despues de su ultima transaccion (si el periodo llega
      // hasta hoy) no se puede juzgar. Si el periodo ya termino, se espera que el reporte lo cubra completo.
      const ultima = Math.max(...transacciones.map((t) => t.ts));
      const primera = Math.min(...transacciones.map((t) => t.ts));
      const periodoTerminado = fechaIso(ahora) > fechaFin;
      const horizonte = periodoTerminado ? finDia : Math.min(finDia, ultima);

      const avisos = [];
      if (periodoTerminado && finDia - ultima > 24 * 3600) avisos.push(`El reporte de Banorte termina el ${fechaHoraMx(ultima)}, mucho antes del fin del periodo. Si no es lo que esperabas, descarga el reporte completo.`);
      if (primera - inicio > 24 * 3600) avisos.push(`El reporte de Banorte empieza hasta el ${fechaHoraMx(primera)}, despues del inicio del periodo. Si no es lo que esperabas, descarga el reporte completo.`);

      const analisis = analizarConciliacion({
        registros: registrosDeBd(json.data), transacciones, horizonte, ahora, inicio, precios, nombresPlanteles,
        fechaCorreccion: fechaMx(ahora), etiqueta: etiquetaDeFechaHora(ahora),
      });
      setResultado({ ...analisis, avisos, horizonte, ahora, periodo: { fechaInicio, fechaFin } });
    } catch (e) {
      console.error("Error al conciliar:", e);
      setError(e && e.message ? e.message : "Ocurrio un problema al conciliar.");
    } finally {
      setTrabajando(false);
    }
  };

  // ------------------------------------------------------------ descargas
  const descargarScripts = () => {
    descargarArchivo(`Conciliacion_scripts_${etiquetaDeFecha(resultado.ahora)}.sql`, scriptsEnUnArchivo(resultado.archivos), "text/plain;charset=utf-8");
  };

  // La libreria de Excel pesa mucho: se carga solo al exportar.
  const armarLibro = async (hojas) => {
    const XLSX = await import("xlsx");
    const libro = XLSX.utils.book_new();
    hojas.forEach(({ nombre, encabezados, datos, anchos }) => {
      const hoja = XLSX.utils.aoa_to_sheet([encabezados, ...datos]);
      hoja["!cols"] = anchos.map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(libro, hoja, nombre);
    });
    return { XLSX, libro };
  };

  const descargarExcelGeneral = async () => {
    try {
      const r = resultado;
      const resumen = [
        ["Periodo", `${r.periodo.fechaInicio} a ${r.periodo.fechaFin}`],
        ["Revisado hasta", fechaHoraMx(r.horizonte)],
        ["Registros PAGADO en la BD", r.resumen.registros],
        ["Transacciones de Banorte (aprobadas / declinadas)", `${r.resumen.aprobadas} / ${r.resumen.declinadas}`],
        ["Correctos (coinciden con una aprobada)", r.resumen.ok],
        ["Dicen PAGADO pero Banorte declino", `${r.resumen.declinadasGuardadas} (${dinero(r.resumen.importeDeclinadas)})`],
        ["Dicen PAGADO pero no hay transaccion", `${r.resumen.sinTransaccion} (${dinero(r.resumen.importeSinTransaccion)})`],
        ["Aprobados por Banorte sin registro", `${r.resumen.aprobadasSinRegistro} (${dinero(r.resumen.importeSinRegistro)})`],
        ["Despues de corregir deben quedar PAGADO", `${r.resumen.esperadoRegistros} registros por ${dinero(r.resumen.esperadoImporte)}`],
        ["Alumnos con anomalias", r.resumen.alumnosConAnomalias],
      ];
      const { XLSX, libro } = await armarLibro([
        { nombre: "Resumen", encabezados: ["Concepto", "Valor"], datos: resumen, anchos: [52, 40] },
        { nombre: "Casos por alumno", encabezados: ENCABEZADOS_CASOS, datos: r.casos, anchos: [8, 26, 16, 38, 60, 45, 60, 45] },
      ]);
      XLSX.writeFile(libro, `Conciliacion_${r.periodo.fechaInicio}_a_${r.periodo.fechaFin}.xlsx`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo generar el archivo de Excel.", "error");
    }
  };

  const descargarPlantel = async (oficial) => {
    try {
      const r = resultado;
      const filas = r.casos.filter((c) => c[0] === oficial).map((c) => c.slice(2));
      const nombre = (nombresPlanteles[oficial] || "plantel").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const { XLSX, libro } = await armarLibro([
        { nombre: "Casos", encabezados: ENCABEZADOS_CASOS.slice(2), datos: filas, anchos: [16, 38, 60, 45, 60, 45] },
      ]);
      XLSX.writeFile(libro, `Conciliacion_plantel_${oficial}_${nombre}_${fechaIso(r.ahora)}.xlsx`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo generar el archivo de Excel.", "error");
    }
  };

  // ------------------------------------------------------------ presentacion
  const r = resultado;
  const planteles = useMemo(() => {
    if (!r) return [];
    const porPlantel = {};
    r.casos.forEach((c) => {
      const p = c[0];
      if (!porPlantel[p]) porPlantel[p] = { oficial: p, nombre: c[1], total: 0, urgentes: 0 };
      porPlantel[p].total += 1;
      if (c[3].startsWith("1-")) porPlantel[p].urgentes += 1;
    });
    return Object.values(porPlantel).sort((a, b) => (a.oficial < b.oficial ? -1 : 1));
  }, [r]);

  const hayErrores = r && (r.falsos.length > 0 || r.sinRegistro.length > 0);
  const hayAlgoQueCorregir = r && (hayErrores || r.folios.length > 0);
  const urgentes = r ? r.casos.filter((c) => c[3].startsWith("1-")).length : 0;

  return (
    <div className="Dashboard-card">
      <h3 style={{ marginBottom: "5px", color: "#003b5c" }}>Conciliación de pagos en línea</h3>
      <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0 }}>
        Compara los pagos en línea guardados en el sistema contra el reporte de Banorte, para encontrar registros que dicen
        “pagado” sin que el banco haya cobrado, y pagos aprobados por el banco que no quedaron registrados.
        Este módulo <b>solo compara</b>: no modifica nada. Entrega los scripts de corrección y los archivos por plantel.
      </p>

      <ol style={{ fontSize: "0.85rem", color: "#555", paddingLeft: "20px", margin: "0 0 12px 0" }}>
        <li>En Multicobros: Analítica → Reportes → <b>Transacciones Payworks 2</b>, con las mismas fechas y <b>sin</b> filtros de monto. Expórtalo a CSV o Excel <b>justo antes</b> de comparar.</li>
        <li>Elige aquí las fechas y el archivo, y pulsa <b>Comparar</b>.</li>
        <li>Revisa los casos, avisa a los planteles con sus archivos y, si procede, corre los scripts en la base de datos.</li>
      </ol>

      <div style={{ display: "flex", gap: "15px", alignItems: "flex-end", flexWrap: "wrap", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "6px", margin: "10px 0" }}>
        <div>
          <label htmlFor="conc-desde" style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>Desde</label>
          <input id="conc-desde" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} style={estiloInput} />
        </div>
        <div>
          <label htmlFor="conc-hasta" style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>Hasta</label>
          <input id="conc-hasta" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} style={estiloInput} />
        </div>
        <div>
          <label htmlFor="conc-archivo" style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>Reporte de Banorte (CSV o Excel)</label>
          <input id="conc-archivo" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setArchivo(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ fontSize: "0.9rem" }} />
        </div>
        <button
          onClick={comparar}
          disabled={rangoInvalido || !archivo || trabajando}
          className="btn-select validar"
          style={{ border: "none", padding: "10px 18px", opacity: (rangoInvalido || !archivo || trabajando) ? 0.45 : 1, cursor: (rangoInvalido || !archivo || trabajando) ? "not-allowed" : "pointer" }}
        >
          {trabajando ? "Comparando..." : "Comparar"}
        </button>
      </div>

      {rangoInvalido && <Aviso>La fecha inicial no puede ser mayor que la final.</Aviso>}
      {error && <Aviso tipo="error">{error}</Aviso>}

      {r && (
        <div>
          <p style={{ fontSize: "0.85rem", color: "#555" }}>
            Periodo {r.periodo.fechaInicio} a {r.periodo.fechaFin}. Se revisó hasta el <b>{fechaHoraMx(r.horizonte)}</b>
            {" "}(consulta hecha el {fechaHoraMx(r.ahora)}).
          </p>

          {r.avisos.map((a) => <Aviso key={a}>{a}</Aviso>)}
          {r.posteriores.length > 0 && <Aviso>{r.posteriores.length} registro(s) de la base de datos son posteriores a la última transacción del reporte de Banorte y no se revisaron. Si el periodo llega hasta hoy, descarga el reporte más reciente y vuelve a comparar.</Aviso>}
          {r.recientes.length > 0 && <Aviso>{r.recientes.length} pago(s) aprobado(s) por Banorte hace menos de 10 minutos no tienen registro todavía; pueden estar guardándose. No se incluyeron: vuelve a comparar en unos minutos.</Aviso>}
          {r.borde.length > 0 && <Aviso>{r.borde.length} registro(s) en los bordes del periodo (primeros o últimos minutos) no tienen transacción en el reporte; pueden ser de una transacción que el reporte no cubre y no se juzgan (ids {r.borde.map((d) => d.id).join(", ")}).</Aviso>}
          {r.problemas.length > 0 && <Aviso tipo="error">No se pudo armar el alta de {r.problemas.length} pago(s) aprobado(s): {r.problemas.map((p) => `${fechaHoraMx(p.b.ts)} ${matDeRef1(p.b.ref1)} (${p.motivo})`).join("; ")}. Revísalos a mano.</Aviso>}
          {r.pruebasUno.length > 0 && <Aviso>{r.pruebasUno.length} pago(s) de $1 se tomaron como pruebas y no se dan de alta.</Aviso>}
          {r.otros.length > 0 && <Aviso>{r.otros.length} registro(s) con otro estatus (por ejemplo CANCELADO por una corrección anterior) no entran en la comparación.</Aviso>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "12px", margin: "14px 0" }}>
            <Tarjeta titulo="Correctos" valor={r.resumen.ok} detalle={`de ${r.resumen.registros} registros revisados`} color="#2e7d32" />
            <Tarjeta titulo="Dicen PAGADO sin serlo" valor={r.falsos.length} detalle={`${dinero(r.resumen.importeDeclinadas + r.resumen.importeSinTransaccion)} (${r.dec.length} declinados, ${r.sin.length} sin transacción)`} color="#c62828" />
            <Tarjeta titulo="Aprobados sin registro" valor={r.resumen.aprobadasSinRegistro} detalle={dinero(r.resumen.importeSinRegistro)} color="#ef6c00" />
            <Tarjeta titulo="Alumnos con anomalías" valor={r.resumen.alumnosConAnomalias} detalle={urgentes ? `${urgentes} NO han pagado (urgente)` : "ninguno sin pagar"} color="#6a1b9a" />
          </div>

          {!hayErrores && <Aviso>✅ Todo cuadra: no hay registros falsos ni pagos aprobados sin registro en lo revisado.</Aviso>}
          {r.folios.length > 0 && <Aviso>{r.folios.length} pago(s) correcto(s) no tienen anotado el número de autorización de Banorte; el script 4 lo anota.</Aviso>}

          {hayErrores && (
            <p style={{ fontSize: "0.9rem" }}>
              Si se corrige todo, los pagos en línea PAGADO del periodo (hasta el corte) deben quedar en <b>{r.resumen.esperadoRegistros}</b> registros
              por <b>{dinero(r.resumen.esperadoImporte)}</b>, que es lo que Banorte aprobó (sin pruebas de $1).
            </p>
          )}

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", margin: "10px 0 20px 0" }}>
            {hayAlgoQueCorregir && (
              <button onClick={descargarScripts} className="btn-select validar" style={{ border: "none", padding: "10px 16px" }}>
                Descargar scripts SQL
              </button>
            )}
            {r.casos.length > 0 && (
              <button onClick={descargarExcelGeneral} className="btn-select" style={{ border: "1px solid #003b5c", padding: "10px 16px", backgroundColor: "#fff", color: "#003b5c" }}>
                Descargar Excel con todos los casos
              </button>
            )}
          </div>

          {hayAlgoQueCorregir && (
            <div style={{ ...estiloTarjeta, backgroundColor: "#f8f9fa", fontSize: "0.85rem", marginBottom: "16px" }}>
              <b>Cómo aplicar los scripts</b> (en la base de datos, un bloque a la vez; cada bloque revisa cuántas filas toca y, si no coincide, no cambia nada):
              <ol style={{ margin: "6px 0 0 0", paddingLeft: "20px" }}>
                <li>Respaldo de las tablas <code>tblmultipagos_intenciones</code> y <code>tblmultipagos_detalles</code>.</li>
                <li><b>1_respaldo</b> → <b>2_cancelar</b> → <b>3_alta</b> → <b>4_rellenar_folio_banco</b>; después <b>5_verificacion</b> (debe dar lo que dice arriba).</li>
                <li>Si algo no cuadra, <b>6_reversa</b>. Antes de correrlos, avisa a los planteles de los alumnos marcados como “NO HA PAGADO”.</li>
              </ol>
            </div>
          )}

          {r.falsos.length > 0 && (
            <div style={{ marginBottom: "18px", overflowX: "auto" }}>
              <h4 className="titulo-seccion">Registros que dicen PAGADO y no lo son ({r.falsos.length})</h4>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["ID", "Matrícula", "Plantel", "Fecha y hora", "Importe", "Motivo"].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
                <tbody>
                  {r.falsos.slice(0, FILAS_VISIBLES).map((d) => (
                    <tr key={d.id}>
                      <td style={estiloTd}>{d.id}</td>
                      <td style={estiloTd}>{d.mat}</td>
                      <td style={estiloTd}>{plantelOficial(d.mat)} {nombresPlanteles[plantelOficial(d.mat)] || ""}</td>
                      <td style={estiloTd}>{fechaHoraMx(d.ts)}</td>
                      <td style={estiloTd}>{dinero(d.monto)}</td>
                      <td style={estiloTd}>{d.clase === "SIN_TRX" ? "Sin transacción en Banorte" : `Banorte declinó: ${d.b.desc}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {r.falsos.length > FILAS_VISIBLES && <p style={{ fontSize: "0.8rem", color: "#666" }}>Se muestran los primeros {FILAS_VISIBLES}; los scripts y el Excel traen todos.</p>}
            </div>
          )}

          {r.sinRegistro.length > 0 && (
            <div style={{ marginBottom: "18px", overflowX: "auto" }}>
              <h4 className="titulo-seccion">Pagos aprobados por Banorte sin registro ({r.sinRegistro.filter((b) => b.monto !== 1.0).length} se dan de alta)</h4>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["Fecha y hora", "Matrícula", "Plantel", "Importe", "Autorización", "Conceptos (claves)"].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
                <tbody>
                  {r.sinRegistro.slice(0, FILAS_VISIBLES).map((b) => (
                    <tr key={`${b.ts}-${b.ref1}`}>
                      <td style={estiloTd}>{fechaHoraMx(b.ts)}</td>
                      <td style={estiloTd}>{matDeRef1(b.ref1)}</td>
                      <td style={estiloTd}>{plantelOficial(matDeRef1(b.ref1))} {nombresPlanteles[plantelOficial(matDeRef1(b.ref1))] || ""}</td>
                      <td style={estiloTd}>{dinero(b.monto)}{b.monto === 1.0 ? " (prueba)" : ""}</td>
                      <td style={estiloTd}>{b.aut}</td>
                      <td style={estiloTd}>{clavesDeRef1(b.ref1).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {planteles.length > 0 && (
            <div style={{ marginBottom: "10px", overflowX: "auto" }}>
              <h4 className="titulo-seccion">Archivos por plantel</h4>
              <p style={{ fontSize: "0.85rem", color: "#555", margin: "0 0 8px 0" }}>Cada archivo trae solo los alumnos de ese plantel, para enviárselo.</p>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["Plantel", "Alumnos con anomalías", "NO han pagado", ""].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
                <tbody>
                  {planteles.map((p) => (
                    <tr key={p.oficial}>
                      <td style={estiloTd}>{p.oficial} {p.nombre}</td>
                      <td style={estiloTd}>{p.total}</td>
                      <td style={estiloTd}>{p.urgentes}</td>
                      <td style={estiloTd}>
                        <button onClick={() => descargarPlantel(p.oficial)} className="btn-select" aria-label={`Descargar archivo del plantel ${p.oficial} ${p.nombre}`} style={{ border: "1px solid #003b5c", padding: "4px 10px", backgroundColor: "#fff", color: "#003b5c" }}>
                          Descargar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VistaConciliacion;
